import { Button, CircularProgress, Link, Typography } from '@mui/material';
import { colors } from '@truvhq/ui';
import { Link as RouterLink } from 'react-router-dom';

import type { ChecklistStep, StepStatus } from 'entities/onboarding/types';
import { toInternalPath } from 'features/onboarding/lib/links';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { MOBILE_BREAKPOINT } from 'shared/lib/screen/useScreenSize';

// A pointer hits the label; a thumb needs the whole control, so on a phone these grow to a
// comfortable target rather than staying the size of their text.
const TOUCH_TARGET = { [`@media (max-width: ${MOBILE_BREAKPOINT - 1}px)`]: { minHeight: 44 } } as const;

type StepActionsProps = {
  step: ChecklistStep;
  blockerTitle?: string | null;
  onOpenBlocker?: () => void;
  busy: StepStatus | null;
  onSettle: (status: StepStatus) => Promise<unknown>;
};

// Routed client-side when the step's page lives inside the dashboard, so following it doesn't spawn
// a second copy of the app.
function StepAction({ link, title, isBlocked }: { link: string; title: string; isBlocked: boolean }) {
  const closePanel = useOnboardingDialogStore((state) => state.close);
  const internalPath = toInternalPath(link);
  const sx = {
    borderRadius: 999,
    textTransform: 'none',
    px: 1.75,
    py: 0.625,
    fontSize: '0.875rem',
    boxShadow: 'none',
    ...TOUCH_TARGET,
    '&.Mui-disabled': {
      backgroundColor: colors.primary.bright,
      color: 'rgba(255, 255, 255, 0.9)',
      cursor: 'not-allowed',
    },
  } as const;
  // Only one step is expanded at a time, so "Open" reads unambiguously on screen; the label names
  // the step for anyone who can't see which card it sits in.
  const label = `Open ${title}`;

  if (isBlocked) {
    return (
      <Button variant="contained" disabled aria-label={label} sx={sx}>
        Open
      </Button>
    );
  }
  if (!internalPath) {
    // Opens in its own tab, so the panel is not in the way of anything and stays as it was.
    return (
      <Button variant="contained" href={link} target="_blank" rel="noopener noreferrer" aria-label={label} sx={sx}>
        Open
      </Button>
    );
  }
  // The step's page is what the user is being sent to look at, and the panel covers it.
  return (
    <Button
      variant="contained"
      component={RouterLink}
      to={internalPath}
      onClick={closePanel}
      aria-label={label}
      sx={sx}
    >
      Open
    </Button>
  );
}

/** Why a step can't be started yet, and the way to the step that unblocks it. */
export function StepGateNote({ blockerTitle, onOpenBlocker }: { blockerTitle: string; onOpenBlocker: () => void }) {
  return (
    <div className="mt-1.5 w-fit rounded-lg bg-grey20 px-[11px] py-2 text-grey60 text-xs leading-[17px]">
      Available after you{' '}
      <Link component="button" type="button" onClick={onOpenBlocker} className="font-medium text-xs">
        {blockerTitle.toLowerCase()}
      </Link>
    </div>
  );
}

// MUI gives a text button its own horizontal padding, which reads as an indent when the button is
// the first thing in the row. Pull the label back level with the title and description above it.
const TEXT_ACTION_PADDING_X = 0.75;
const textActionSx = (isFirst: boolean) =>
  ({
    px: TEXT_ACTION_PADDING_X,
    py: 0.875,
    ml: isFirst ? -TEXT_ACTION_PADDING_X : 0,
    fontSize: '0.875rem',
    textTransform: 'none',
    ...TOUCH_TARGET,
  }) as const;

/**
 * The step's own page, then the quieter way to tick it off by hand — or to take that back.
 *
 * A lock blocks the page — there is nothing to do there yet — but not the tick: someone who did the
 * work another way can still record it. A step the backend settles for itself is the exception and
 * says so with `manually_completable`. That governs the undo as much as the tick: there is no way to
 * buy a subscription by hand, so there is none to un-buy one either, and either direction would only
 * record a checklist that disagrees with the account.
 */
export function ChecklistStepActions({ step, blockerTitle, onOpenBlocker, busy, onSettle }: StepActionsProps) {
  const isSettled = step.status === 'complete' || step.status === 'skipped';
  const isManual = step.manually_completable !== false;

  if (isSettled) {
    return (
      <>
        {/* Company-level steps settle for everyone, so a teammate's action would otherwise look like
            the step ticked itself off. */}
        {step.completed_by && (
          <Typography variant="body2" className="mt-2 text-grey60 text-xs">
            {step.status === 'skipped' ? 'Skipped' : 'Completed'} by {step.completed_by}
          </Typography>
        )}
        {isManual && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button variant="text" sx={textActionSx(true)} disabled={busy !== null} onClick={() => onSettle('pending')}>
              {busy === 'pending' ? <CircularProgress size={14} color="inherit" /> : 'Undo'}
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {step.locked && blockerTitle && onOpenBlocker && (
        <StepGateNote blockerTitle={blockerTitle} onOpenBlocker={onOpenBlocker} />
      )}
      {(step.link || isManual) && (
        <div className="mt-3.5 flex flex-wrap items-center gap-2">
          {step.link && <StepAction link={step.link} title={step.title} isBlocked={Boolean(step.locked)} />}
          {isManual && (
            <Button
              variant="text"
              sx={textActionSx(!step.link)}
              disabled={busy !== null}
              onClick={() => onSettle('complete')}
            >
              {busy === 'complete' ? <CircularProgress size={14} color="inherit" /> : 'Mark done'}
            </Button>
          )}
        </div>
      )}
    </>
  );
}
